import { Fragment, useState } from "react";
import { toast } from "sonner";
import { MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { RiskBadge, StatusBadge, Empty } from "@/components/app/ui";
import { ValueDialog } from "@/components/discovery/ValueDialog";
import { api, errMsg, can } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CONF = { High: "text-emerald-700", Medium: "text-amber-700", Low: "text-slate-500" };

function Actions({ r, onChange, onPreview }) {
  const { user } = useAuth();
  const act = async (path, msg) => { try { await api.post(`/discovery/findings/${r.id}/${path}`); toast.success(msg); onChange(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`pii-actions-${r.id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onPreview} data-testid={`pii-preview-${r.id}`}>Safe preview (masked)</DropdownMenuItem>
        {can(user, "finding") && !r.finding_id && <DropdownMenuItem onClick={() => act("finding", "Finding and remediation task created")} data-testid={`pii-finding-${r.id}`}>Create finding + remediation</DropdownMenuItem>}
        {can(user, "inventory") && !r.inventory_id && <DropdownMenuItem onClick={() => act("inventory", "Added to data inventory")} data-testid={`pii-inventory-${r.id}`}>Add to data inventory</DropdownMenuItem>}
        {can(user, "datasource") && r.status !== "Dismissed" && <DropdownMenuItem onClick={() => act("dismiss", "Marked as not personal data")} data-testid={`pii-dismiss-${r.id}`}>Dismiss (false positive)</DropdownMenuItem>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function PiiResults({ rows, onChange }) {
  const [open, setOpen] = useState(null);
  const [prev, setPrev] = useState(null);
  if (!rows.length) return <Empty text="No personal data detected yet. Run a scan to see results." />;
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
      <table className="w-full text-sm" data-testid="pii-results-table">
        <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500"><tr>{["Location", "PII type", "Confidence", "Match", "Risk", "DPDP requirements", "Status", ""].map((h) => <th key={h} className="px-3 py-2.5">{h}</th>)}</tr></thead>
        <tbody>{rows.map((r) => (<Fragment key={r.id}>
          <tr className="cursor-pointer border-t hover:bg-slate-50" onClick={() => setOpen(open === r.id ? null : r.id)} data-testid={`pii-row-${r.id}`}>
            <td className="px-3 py-2"><div className="font-mono text-xs text-slate-800">{r.asset}</div><div className="font-mono text-[11px] text-blue-700">{r.column}</div></td>
            <td className="px-3 py-2"><div className="text-slate-800">{r.category_label}</div><div className="text-[11px] text-slate-500">{r.group} · {r.detection}</div></td>
            <td className={`px-3 py-2 text-xs font-semibold ${CONF[r.confidence]}`}>{r.confidence}</td>
            <td className="px-3 py-2 text-xs text-slate-600">{Math.round(r.match_ratio * 100)}% <span className="text-slate-400">of {r.non_null_sampled}</span></td>
            <td className="px-3 py-2"><RiskBadge risk={r.risk} /></td>
            <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{r.dpdp_controls.join(", ")}</td>
            <td className="px-3 py-2"><StatusBadge status={r.status} />{r.finding_id && <div className="font-mono text-[10px] text-slate-400">{r.finding_id}</div>}</td>
            <td className="px-2" onClick={(e) => e.stopPropagation()}><Actions r={r} onChange={onChange} onPreview={() => setPrev(r)} /></td>
          </tr>
          {open === r.id && <tr key={`${r.id}-d`} className="bg-blue-50/40"><td colSpan={8} className="px-4 py-3 text-xs text-slate-700" data-testid={`pii-detail-${r.id}`}>
            <b>Recommended remediation:</b> {r.recommendation} <span className="text-slate-500">· Source: {r.source_name} ({r.environment}) · Sensitivity: {r.sensitivity} · {r.rows_sampled} rows sampled, none retained.</span></td></tr>}
        </Fragment>))}</tbody>
      </table>
      <ValueDialog result={prev} onClose={() => setPrev(null)} />
    </div>
  );
}
